/**
 * Invoice List Modal Component
 * Generic drill-down modal for showing invoices or bank transactions as evidence.
 * Supports configurable columns, client-side search/filter, and sortable headers.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { fetchInvoices, fetchBankTransactions } from '@/api/metricsApi';
import { formatCurrency, formatDate } from '@/utils/formatters';
import { getStatusDisplay } from '@/utils/invoiceStatus';
import { useTableSort } from '@/hooks/useTableSort';
import { InvoiceFilters } from './InvoiceFilters';
import { BankTransactionsTable } from './BankTransactionsTable';
import type { ClientInvoice } from '@/types/invoices';
import type { BankTransaction } from '@/types/invoices';
import { SortableHeader } from './SortableHeader';
import './InvoiceListModal.css';

export type ModalVariant =
  | 'default'
  | 'cashflow'
  | 'overdue'
  | 'velocity'
  | 'bankTransactions';

interface InvoiceListModalProps {
  title: string;
  queryParams: Record<string, string>;
  variant?: ModalVariant;
  onClose: () => void;
  children?: React.ReactNode;
}

export function InvoiceListModal({
  title,
  queryParams,
  variant = 'default',
  onClose,
  children,
}: InvoiceListModalProps) {
  const [invoices, setInvoices] = useState<ClientInvoice[]>([]);
  const [bankTransactions, setBankTransactions] = useState<BankTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const isBankMode = variant === 'bankTransactions';

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (isBankMode) {
        const response = await fetchBankTransactions(queryParams);
        setBankTransactions(response.data);
      } else {
        const response = await fetchInvoices(queryParams);
        setInvoices(response.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [queryParams, isBankMode]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    previousFocusRef.current = document.activeElement as HTMLElement;
    modalRef.current?.focus();
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = '';
      previousFocusRef.current?.focus();
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const now = new Date();

  const calculateDaysOverdue = (dueDate: string): number => {
    const due = new Date(dueDate);
    return Math.floor((now.getTime() - due.getTime()) / (1000 * 60 * 60 * 24));
  };

  const calculateDaysToPay = (invoiceDate: string, dueDate: string): number => {
    const inv = new Date(invoiceDate);
    const paid = new Date(dueDate);
    return Math.floor((paid.getTime() - inv.getTime()) / (1000 * 60 * 60 * 24));
  };

  // Apply variant-level filter (overdue only)
  const variantFiltered = variant === 'overdue'
    ? invoices.filter((inv) => {
        const due = new Date(inv.dueDate);
        return inv.status === 'AUTHORISED' && due < now;
      })
    : invoices;

  // Apply user search + status filter
  const userFiltered = useMemo(() => {
    let result = variantFiltered;
    if (statusFilter !== 'All') {
      result = result.filter((inv) => inv.status === statusFilter);
    }
    if (searchText.trim()) {
      const term = searchText.toLowerCase();
      result = result.filter(
        (inv) =>
          inv.invoiceNumber.toLowerCase().includes(term) ||
          inv.contact.name.toLowerCase().includes(term)
      );
    }
    return result;
  }, [variantFiltered, statusFilter, searchText]);

  const totalAmount = isBankMode
    ? bankTransactions.reduce((sum, tx) => sum + tx.total, 0)
    : userFiltered.reduce((sum, inv) => {
        if (variant === 'cashflow' || variant === 'overdue') return sum + inv.amountDue;
        // Use amountDue (inc. GST) for outstanding invoices to match card totals
        if (inv.status === 'AUTHORISED' && inv.amountDue > 0) return sum + inv.amountDue;
        return sum + inv.total;
      }, 0);

  const rawCount = isBankMode ? bankTransactions.length : variantFiltered.length;
  const itemCount = isBankMode ? bankTransactions.length : userFiltered.length;
  const showFilters = !isBankMode && variantFiltered.length > 0;

  return (
    <div
      className="invoice-modal-overlay"
      onClick={handleBackdropClick}
      role="presentation"
    >
      <div
        className="invoice-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        ref={modalRef}
        tabIndex={-1}
      >
        <div className="invoice-modal__header">
          <h2 className="invoice-modal__title">{title}</h2>
          <button
            className="invoice-modal__close"
            onClick={onClose}
            aria-label="Close modal"
          >
            &times;
          </button>
        </div>

        {loading && (
          <div className="invoice-modal__loading">
            <div className="loading-spinner"></div>
            <p>Loading data...</p>
          </div>
        )}

        {error && (
          <div className="invoice-modal__error">
            <p>{error}</p>
            <button className="invoice-modal__retry" onClick={loadData}>
              Retry
            </button>
          </div>
        )}

        {!loading && !error && rawCount === 0 && (
          <div className="invoice-modal__empty">
            <p className="invoice-modal__empty-text">
              No {isBankMode ? 'transactions' : 'invoices'} found matching these criteria.
            </p>
          </div>
        )}

        {!loading && !error && rawCount > 0 && (
          <>
            {showFilters && (
              <InvoiceFilters
                searchText={searchText}
                onSearchChange={setSearchText}
                statusFilter={statusFilter}
                onStatusChange={setStatusFilter}
                totalCount={variantFiltered.length}
                filteredCount={userFiltered.length}
              />
            )}

            {itemCount === 0 ? (
              <div className="invoice-modal__empty">
                <p className="invoice-modal__empty-text">
                  No invoices match your filter. Try adjusting your search or status.
                </p>
              </div>
            ) : (
              <div className="invoice-modal__body">
                {isBankMode ? (
                  <BankTransactionsTable transactions={bankTransactions} />
                ) : (
                  <InvoiceTable
                    invoices={userFiltered}
                    variant={variant}
                    calculateDaysOverdue={calculateDaysOverdue}
                    calculateDaysToPay={calculateDaysToPay}
                  />
                )}
              </div>
            )}

            {children && (
              <div className="invoice-modal__extra">
                {children}
              </div>
            )}

            <div className="invoice-modal__footer">
              <span className="invoice-modal__count">
                {itemCount} {isBankMode ? 'transaction' : 'invoice'}{itemCount !== 1 ? 's' : ''}
              </span>
              <span className="invoice-modal__total">
                <span className="invoice-modal__total-label">Total</span>
                {formatCurrency(totalAmount)}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function InvoiceTable({
  invoices,
  variant,
  calculateDaysOverdue,
  calculateDaysToPay,
}: {
  invoices: ClientInvoice[];
  variant: ModalVariant;
  calculateDaysOverdue: (dueDate: string) => number;
  calculateDaysToPay: (invoiceDate: string, dueDate: string) => number;
}) {
  const keyExtractors = useMemo(() => ({
    contactName: (inv: ClientInvoice) => inv.contact.name,
  }), []);
  const { sorted, sortState, toggleSort } = useTableSort(invoices, undefined, keyExtractors);

  if (variant === 'cashflow') {
    return (
      <table className="invoice-modal__table">
        <thead>
          <tr>
            <SortableHeader label="Invoice #" sortKey="invoiceNumber" sortState={sortState} onToggle={toggleSort} />
            <SortableHeader label="Client" sortKey="contactName" sortState={sortState} onToggle={toggleSort} />
            <SortableHeader label="Due Date" sortKey="dueDate" sortState={sortState} onToggle={toggleSort} />
            <th>Status</th>
            <SortableHeader label="Amount Due" sortKey="amountDue" sortState={sortState} onToggle={toggleSort} alignRight />
          </tr>
        </thead>
        <tbody>
          {sorted.map((inv) => {
            const status = getStatusDisplay(inv.status);
            return (
              <tr key={inv.invoiceID}>
                <td>{inv.invoiceNumber}</td>
                <td>{inv.contact.name}</td>
                <td>{formatDate(inv.dueDate, 'dd MMM yyyy')}</td>
                <td>
                  <span className={`invoice-modal__status ${status.cssClass}`}>
                    {status.label}
                  </span>
                </td>
                <td className="invoice-modal__amount">{formatCurrency(inv.amountDue)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }

  if (variant === 'overdue') {
    return (
      <table className="invoice-modal__table">
        <thead>
          <tr>
            <SortableHeader label="Invoice #" sortKey="invoiceNumber" sortState={sortState} onToggle={toggleSort} />
            <SortableHeader label="Client" sortKey="contactName" sortState={sortState} onToggle={toggleSort} />
            <SortableHeader label="Due Date" sortKey="dueDate" sortState={sortState} onToggle={toggleSort} />
            <th>Days Overdue</th>
            <SortableHeader label="Amount Due" sortKey="amountDue" sortState={sortState} onToggle={toggleSort} alignRight />
          </tr>
        </thead>
        <tbody>
          {sorted.map((inv) => (
            <tr key={inv.invoiceID}>
              <td>{inv.invoiceNumber}</td>
              <td>{inv.contact.name}</td>
              <td>{formatDate(inv.dueDate, 'dd MMM yyyy')}</td>
              <td className="invoice-modal__overdue-days">
                {calculateDaysOverdue(inv.dueDate)}
              </td>
              <td className="invoice-modal__amount">{formatCurrency(inv.amountDue)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  if (variant === 'velocity') {
    return (
      <table className="invoice-modal__table">
        <thead>
          <tr>
            <SortableHeader label="Invoice #" sortKey="invoiceNumber" sortState={sortState} onToggle={toggleSort} />
            <SortableHeader label="Client" sortKey="contactName" sortState={sortState} onToggle={toggleSort} />
            <SortableHeader label="Invoice Date" sortKey="date" sortState={sortState} onToggle={toggleSort} />
            <th>Paid Date</th>
            <th>Days to Pay</th>
            <SortableHeader label="Amount" sortKey="subTotal" sortState={sortState} onToggle={toggleSort} alignRight />
          </tr>
        </thead>
        <tbody>
          {sorted.map((inv) => (
            <tr key={inv.invoiceID}>
              <td>{inv.invoiceNumber}</td>
              <td>{inv.contact.name}</td>
              <td>{formatDate(inv.date, 'dd MMM yyyy')}</td>
              <td>{formatDate(inv.dueDate, 'dd MMM yyyy')}</td>
              <td>{calculateDaysToPay(inv.date, inv.dueDate)}</td>
              <td className="invoice-modal__amount">{formatCurrency(inv.subTotal)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  // Default view - use amountDue for outstanding, total (inc. GST) for paid
  const hasOutstanding = invoices.some((inv) => inv.status === 'AUTHORISED' && inv.amountDue > 0);
  const amountLabel = hasOutstanding ? 'Amount Due (inc. GST)' : 'Amount (inc. GST)';

  return (
    <table className="invoice-modal__table">
      <thead>
        <tr>
          <SortableHeader label="Invoice #" sortKey="invoiceNumber" sortState={sortState} onToggle={toggleSort} />
          <SortableHeader label="Client" sortKey="contactName" sortState={sortState} onToggle={toggleSort} />
          <SortableHeader label="Date" sortKey="date" sortState={sortState} onToggle={toggleSort} />
          <th>Status</th>
          <SortableHeader label={amountLabel} sortKey="amountDue" sortState={sortState} onToggle={toggleSort} alignRight />
        </tr>
      </thead>
      <tbody>
        {sorted.map((inv) => {
          const status = getStatusDisplay(inv.status);
          const amount = (inv.status === 'AUTHORISED' && inv.amountDue > 0)
            ? inv.amountDue
            : inv.total;
          return (
            <tr key={inv.invoiceID}>
              <td>{inv.invoiceNumber}</td>
              <td>{inv.contact.name}</td>
              <td>{formatDate(inv.date, 'dd MMM yyyy')}</td>
              <td>
                <span className={`invoice-modal__status ${status.cssClass}`}>
                  {status.label}
                </span>
              </td>
              <td className="invoice-modal__amount">{formatCurrency(amount)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
